// Cross-zone leading patterns — ONE multi-series line chart built exclusively
// from published crossZoneLeaders[].timeline.points (WP10 selection,
// selectionRankWithinZone = 1). Legend pattern names link to the canonical
// pattern route. Horizon prop slices visible points only.
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const ZONE_COLORS = {
    zone_investment_products: "hsl(215 65% 50%)",
    zone_asset_control: "hsl(160 55% 42%)",
    zone_market_access: "hsl(268 55% 58%)",
    zone_digital_money: "hsl(30 80% 52%)",
    zone_balance_sheet: "hsl(350 65% 55%)",
    zone_governance: "hsl(190 60% 45%)",
};
export const zoneColor = (token) => {
    if (ZONE_COLORS[token]) return ZONE_COLORS[token];
    let h = 0;
    for (const c of token || "") h = (h * 31 + c.charCodeAt(0)) % 360;
    return `hsl(${h} 55% 50%)`;
};

export const LeadersChart = ({ leaders, horizonWeeks }) => {
    const { rows, series } = useMemo(() => {
        const weekSet = new Set();
        for (const l of leaders || []) for (const p of l.timeline?.points || []) weekSet.add(p.yearWeek);
        const allWeeks = [...weekSet].sort();
        const visible = allWeeks.slice(-(horizonWeeks || 12));   // display slicing only
        const rowsByWeek = visible.map((w) => ({ yearWeek: w }));
        const seriesDefs = [];
        for (const l of leaders || []) {
            const key = l.marketZoneKey;
            seriesDefs.push({
                key,
                color: zoneColor(l.presentationColorToken),
                displayName: l.displayTitle || l.patternLabel,
                patternLabel: l.patternLabel,
                zoneLabel: l.marketZoneLabel,
                canonicalPatternId: l.canonicalPatternId,
            });
            const byWeek = Object.fromEntries((l.timeline?.points || []).map((p) => [p.yearWeek, p.displayScoreExact]));
            for (const row of rowsByWeek) row[key] = byWeek[row.yearWeek] ?? null;
        }
        return { rows: rowsByWeek, series: seriesDefs };
    }, [leaders, horizonWeeks]);

    if (!series.length) {
        return <div className="card-flat p-5 text-xs text-[hsl(var(--mute))]">No cross-zone leaders published in this package.</div>;
    }

    return (
        <div className="card-flat p-4 md:p-5" data-testid="wp12-home-leaders-chart">
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -6 }}>
                        <CartesianGrid stroke="hsl(var(--line))" strokeDasharray="2 4" vertical={false} />
                        <XAxis dataKey="yearWeek" tick={{ fontSize: 9, fill: "hsl(var(--mute))" }}
                               tickFormatter={(w) => w.replace("2026-", "")} tickLine={false} axisLine={false} />
                        <YAxis domain={["auto", 100]} allowDataOverflow tick={{ fontSize: 9, fill: "hsl(var(--mute))" }}
                               tickFormatter={(v) => Math.round(v)} tickLine={false} axisLine={false} width={38} />
                        <Tooltip
                            contentStyle={{ fontSize: 10, borderRadius: 6, border: "1px solid hsl(var(--line))" }}
                            formatter={(value, key) => {
                                const s = series.find((x) => x.key === key);
                                return [value != null ? Number(value).toFixed(1) : "—", s ? s.displayName : key];
                            }}
                        />
                        <Legend content={() => null} />
                        {series.map((s) => (
                            <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.8}
                                  dot={false} activeDot={{ r: 3 }} connectNulls />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
            {/* Legend: published pattern label with zone context */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1.5" data-testid="wp12-home-leaders-legend">
                {series.map((s) => (
                    <div key={s.key} className="flex items-center gap-2 min-w-0" data-testid={`wp12-home-leader-${s.key}`}>
                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                        <span className="text-[10px] leading-tight min-w-0">
                            <Link
                                to={`/market-v2/patterns/${encodeURIComponent(s.canonicalPatternId)}`}
                                data-testid={`wp12-home-leader-link-${s.key}`}
                                className="font-medium text-[hsl(var(--ink))] truncate block hover:text-[hsl(var(--accent-ink))] hover:underline"
                                title={s.displayName !== s.patternLabel ? `${s.displayName} · ${s.patternLabel}` : s.patternLabel}
                            >{s.displayName}</Link>
                            <span className="text-[hsl(var(--mute))]">{s.zoneLabel}</span>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};
